import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { consumeLimitedAction } from "@/lib/usageLimits";
import { showBrowserNotification } from "@/lib/browserNotifications";

export type CallType = "audio" | "video";
export type CallStatus = "idle" | "ringing" | "incoming" | "connecting" | "active" | "ending";

export interface CallParticipant {
  id: string;
  name: string;
  role: string;
}

interface CallSessionRecord {
  id: string;
  caller_id: string;
  recipient_id: string;
  call_type: CallType;
  status: string;
  created_at: string;
}

interface CallSignalRecord {
  id: string;
  call_session_id: string;
  sender_id: string;
  recipient_id: string;
  signal_type: string;
  payload: Record<string, unknown> | null;
}

export interface ActiveCall {
  sessionId: string;
  partnerId: string;
  partnerName: string;
  partnerRole: string;
  direction: "incoming" | "outgoing";
  callType: CallType;
  status: CallStatus;
  createdAt: string;
}

const rtcConfiguration: RTCConfiguration = {
  iceServers: [
    { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
  ],
};

const MISSED_CALL_TIMEOUT_MS = 45_000;

function toErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Please try again.";
}

export function useCalls(participants: CallParticipant[]) {
  const { user, profile } = useAuth();
  const participantsById = useMemo(
    () => new Map(participants.map((participant) => [participant.id, participant])),
    [participants],
  );

  const [incomingCall, setIncomingCall] = useState<ActiveCall | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const missedCallTimerRef = useRef<number | null>(null);
  const queuedIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const ringtoneIntervalRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const endingSessionIdsRef = useRef<Set<string>>(new Set());
  const answeredSessionIdsRef = useRef<Set<string>>(new Set());
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);

  const browserPushEnabled = Boolean(profile?.browser_push_enabled);

  const browserSupportsCalling = typeof window !== "undefined"
    && typeof window.RTCPeerConnection !== "undefined"
    && typeof navigator !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia;

  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  useEffect(() => {
    remoteStreamRef.current = remoteStream;
  }, [remoteStream]);

  const resolveParticipant = useCallback((partnerId: string) => {
    const participant = participantsById.get(partnerId);
    return {
      name: participant?.name || "PropTRUST user",
      role: participant?.role || "client",
    };
  }, [participantsById]);

  const clearMissedCallTimer = useCallback(() => {
    if (missedCallTimerRef.current) {
      window.clearTimeout(missedCallTimerRef.current);
      missedCallTimerRef.current = null;
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneIntervalRef.current) {
      window.clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
  }, []);

  const playRingtonePulse = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) {
      return;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextConstructor();
    }

    const context = audioContextRef.current;
    if (context.state === "suspended") {
      void context.resume().catch(() => undefined);
    }

    const scheduleTone = (startAt: number, frequency: number, duration: number) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, startAt);
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(0.22, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(startAt);
      oscillator.stop(startAt + duration + 0.02);
    };

    const now = context.currentTime + 0.01;
    scheduleTone(now, 880, 0.22);
    scheduleTone(now + 0.32, 660, 0.26);
  }, []);

  const startRingtone = useCallback(() => {
    if (ringtoneIntervalRef.current || typeof window === "undefined") {
      return;
    }

    playRingtonePulse();
    ringtoneIntervalRef.current = window.setInterval(() => {
      playRingtonePulse();
    }, 1800);
  }, [playRingtonePulse]);

  const teardownConnection = useCallback((stopLocalTracks = true) => {
    clearMissedCallTimer();
    stopRingtone();
    queuedIceCandidatesRef.current = [];
    endingSessionIdsRef.current.clear();
    answeredSessionIdsRef.current.clear();

    if (peerConnectionRef.current) {
      peerConnectionRef.current.onicecandidate = null;
      peerConnectionRef.current.ontrack = null;
      peerConnectionRef.current.onconnectionstatechange = null;
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (stopLocalTracks) {
      localStreamRef.current?.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }

    remoteStreamRef.current?.getTracks().forEach((track) => track.stop());
    setRemoteStream(null);
    setIsMuted(false);
    setIsCameraEnabled(true);
  }, [clearMissedCallTimer, stopRingtone]);

  const sendSignal = useCallback(async (
    callSessionId: string,
    recipientId: string,
    signalType: "offer" | "answer" | "ice-candidate",
    payload: Record<string, unknown>,
  ) => {
    if (!user) {
      throw new Error("Not authenticated");
    }

    const { data, error } = await supabase.functions.invoke("send-call-signal", {
      body: {
        call_session_id: callSessionId,
        recipient_id: recipientId,
        signal_type: signalType,
        payload,
      },
    });

    if (error || data?.error) {
      console.error("send-call-signal failed", error || data?.error);
      throw error || new Error(data?.error || "Could not send call signal.");
    }
  }, [user]);

  const attachLocalTracks = useCallback((stream: MediaStream) => {
    const connection = peerConnectionRef.current;
    if (!connection) {
      return;
    }

    const senderKinds = new Set(connection.getSenders().map((sender) => sender.track?.kind).filter(Boolean));
    stream.getTracks().forEach((track) => {
      if (!senderKinds.has(track.kind)) {
        connection.addTrack(track, stream);
      }
    });
  }, []);

  const updateSessionStatus = useCallback(async (callSessionId: string, action: "accept" | "decline" | "end" | "missed") => {
    const { data, error } = await supabase.functions.invoke("update-call-session", {
      body: {
        call_session_id: callSessionId,
        action,
      },
    });

    if (error) {
      throw error;
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return data?.session as CallSessionRecord;
  }, []);

  const createPeerConnection = useCallback((callSessionId: string, partnerId: string) => {
    if (peerConnectionRef.current) {
      const existingConnection = peerConnectionRef.current;
      if (
        existingConnection.signalingState !== "closed"
        && !["closed", "failed"].includes(existingConnection.connectionState)
      ) {
        return existingConnection;
      }

      existingConnection.onicecandidate = null;
      existingConnection.ontrack = null;
      existingConnection.onconnectionstatechange = null;
      existingConnection.close();
      peerConnectionRef.current = null;
    }

    const connection = new RTCPeerConnection(rtcConfiguration);

    connection.onicecandidate = (event) => {
      if (!event.candidate) {
        return;
      }

      void sendSignal(callSessionId, partnerId, "ice-candidate", {
        candidate: event.candidate.toJSON(),
      });
    };

    connection.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) {
        setRemoteStream(stream);
      }
    };

    connection.onconnectionstatechange = () => {
      if (connection.connectionState === "connected") {
        clearMissedCallTimer();
        setActiveCall((current) => current ? { ...current, status: "active" } : current);
      }

      if (connection.connectionState === "disconnected") {
        setActiveCall((current) => current ? { ...current, status: "connecting" } : current);
        return;
      }

      if (["failed", "closed"].includes(connection.connectionState)) {
        setActiveCall((current) => current ? { ...current, status: "ending" } : current);
        if (!endingSessionIdsRef.current.has(callSessionId)) {
          endingSessionIdsRef.current.add(callSessionId);
          void updateSessionStatus(callSessionId, "end").catch(() => undefined);
        }
      }
    };

    peerConnectionRef.current = connection;
    return connection;
  }, [clearMissedCallTimer, sendSignal, updateSessionStatus]);

  const requestLocalMedia = useCallback(async (callType: CallType) => {
    if (!browserSupportsCalling) {
      throw new Error("Calling is not supported in this browser.");
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: callType === "video",
    });

    setLocalStream(stream);
    setIsMuted(false);
    setIsCameraEnabled(callType === "video");
    return stream;
  }, [browserSupportsCalling]);

  const markCallStateEnded = useCallback((title: string, description?: string) => {
    toast(title, description ? { description } : undefined);
    teardownConnection();
    setIncomingCall(null);
    setActiveCall(null);
  }, [teardownConnection]);

  const applyQueuedCandidates = useCallback(async () => {
    const connection = peerConnectionRef.current;
    if (!connection || !connection.remoteDescription) {
      return;
    }

    const candidates = [...queuedIceCandidatesRef.current];
    queuedIceCandidatesRef.current = [];

    for (const candidate of candidates) {
      await connection.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }, []);

  const loadSignal = useCallback(async (
    callSessionId: string,
    signalType: "offer" | "answer",
    maxAttempts = 24,
  ) => {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const { data, error } = await supabase
        .from("call_signals")
        .select("id, call_session_id, sender_id, recipient_id, signal_type, payload")
        .eq("call_session_id", callSessionId)
        .eq("signal_type", signalType)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        return data as CallSignalRecord;
      }

      await new Promise((resolve) => window.setTimeout(resolve, 350));
    }

    throw new Error(`Could not retrieve the ${signalType} signal.`);
  }, []);

  const handleIncomingSignal = useCallback(async (signal: CallSignalRecord) => {
    if (!activeCall || signal.call_session_id !== activeCall.sessionId) {
      return;
    }

    const connection = peerConnectionRef.current;
    if (!connection) {
      return;
    }

    if (signal.signal_type === "answer" && signal.payload?.sdp && signal.payload?.type) {
      if (!connection.currentRemoteDescription) {
        await connection.setRemoteDescription(new RTCSessionDescription({
          type: String(signal.payload.type) as RTCSdpType,
          sdp: String(signal.payload.sdp),
        }));
        await applyQueuedCandidates();
      }
      return;
    }

    if (signal.signal_type === "ice-candidate" && signal.payload?.candidate) {
      const candidate = signal.payload.candidate as RTCIceCandidateInit;
      if (connection.remoteDescription) {
        await connection.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        queuedIceCandidatesRef.current.push(candidate);
      }
    }
  }, [activeCall, applyQueuedCandidates]);

  const startCall = useCallback(async (partnerId: string, callType: CallType) => {
    if (!user) {
      throw new Error("Not authenticated");
    }

    if (!browserSupportsCalling) {
      throw new Error("Calling is not supported in this browser.");
    }

    await consumeLimitedAction('call_start', 'call');

    const participant = resolveParticipant(partnerId);
    const stream = await requestLocalMedia(callType);
    let session: CallSessionRecord | null = null;

    try {
      const { data, error } = await supabase.functions.invoke("create-call-session", {
        body: {
          recipient_id: partnerId,
          call_type: callType,
          force_restart: true,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.session) {
        throw new Error(data?.error || "Could not start the call.");
      }

      session = data.session as CallSessionRecord;
      setActiveCall({
        sessionId: session.id,
        partnerId,
        partnerName: participant.name,
        partnerRole: participant.role,
        direction: "outgoing",
        callType,
        status: "ringing",
        createdAt: session.created_at,
      });

      const connection = createPeerConnection(session.id, partnerId);
      attachLocalTracks(stream);

      const offer = await connection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: callType === "video",
      });
      await connection.setLocalDescription(offer);

      await sendSignal(session.id, partnerId, "offer", {
        type: offer.type,
        sdp: offer.sdp,
      });

      void (async () => {
        try {
          const answerSignal = await loadSignal(session.id, "answer", 90);
          if (!peerConnectionRef.current?.currentRemoteDescription && answerSignal.payload?.sdp && answerSignal.payload?.type) {
            await connection.setRemoteDescription(new RTCSessionDescription({
              type: String(answerSignal.payload.type) as RTCSdpType,
              sdp: String(answerSignal.payload.sdp),
            }));
            await applyQueuedCandidates();
          }
        } catch (answerError) {
          console.error("Could not hydrate answer signal", answerError);
        }
      })();

      clearMissedCallTimer();
      missedCallTimerRef.current = window.setTimeout(() => {
        void updateSessionStatus(session.id, "missed").catch(() => undefined);
      }, MISSED_CALL_TIMEOUT_MS);
    } catch (error) {
      console.error("startCall failed", error);
      if (session?.id) {
        await updateSessionStatus(session.id, "end").catch(() => undefined);
      }
      teardownConnection();
      setActiveCall(null);
      throw error;
    }
  }, [
    attachLocalTracks,
    applyQueuedCandidates,
    browserSupportsCalling,
    clearMissedCallTimer,
    createPeerConnection,
    loadSignal,
    requestLocalMedia,
    resolveParticipant,
    sendSignal,
    teardownConnection,
    updateSessionStatus,
    user,
  ]);

  const acceptIncomingCall = useCallback(async () => {
    if (!incomingCall) {
      return;
    }

    const sessionId = incomingCall.sessionId;
    const partnerId = incomingCall.partnerId;
    try {
      setActiveCall({
        ...incomingCall,
        status: "connecting",
      });
      setIncomingCall(null);

      const stream = await requestLocalMedia(incomingCall.callType);
      const connection = createPeerConnection(sessionId, partnerId);
      attachLocalTracks(stream);

      const offerSignal = await loadSignal(sessionId, "offer", 40);
      if (!offerSignal.payload?.sdp || !offerSignal.payload?.type) {
        throw new Error("Incoming call offer was incomplete.");
      }

      await connection.setRemoteDescription(new RTCSessionDescription({
        type: String(offerSignal.payload.type) as RTCSdpType,
        sdp: String(offerSignal.payload.sdp),
      }));
      await applyQueuedCandidates();

      const answer = await connection.createAnswer();
      await connection.setLocalDescription(answer);

      await sendSignal(sessionId, partnerId, "answer", {
        type: answer.type,
        sdp: answer.sdp,
      });

      if (!answeredSessionIdsRef.current.has(sessionId)) {
        answeredSessionIdsRef.current.add(sessionId);
        await updateSessionStatus(sessionId, "accept");
      }
    } catch (error) {
      console.error("acceptIncomingCall failed", error);
      await updateSessionStatus(sessionId, "end").catch(() => undefined);
      teardownConnection();
      setActiveCall(null);
      setIncomingCall(null);
      throw error;
    }
  }, [
    applyQueuedCandidates,
    attachLocalTracks,
    createPeerConnection,
    incomingCall,
    loadSignal,
    requestLocalMedia,
    sendSignal,
    teardownConnection,
    updateSessionStatus,
  ]);

  const declineIncomingCall = useCallback(async () => {
    if (!incomingCall) {
      return;
    }

    await updateSessionStatus(incomingCall.sessionId, "decline");
    setIncomingCall(null);
  }, [incomingCall, updateSessionStatus]);

  const endActiveCall = useCallback(async () => {
    if (!activeCall) {
      return;
    }

    setActiveCall((current) => current ? { ...current, status: "ending" } : current);
    await updateSessionStatus(activeCall.sessionId, "end").catch(() => undefined);
    teardownConnection();
    setActiveCall(null);
  }, [activeCall, teardownConnection, updateSessionStatus]);

  const toggleMute = useCallback(() => {
    if (!localStream) {
      return;
    }

    const nextMuted = !isMuted;
    localStream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setIsMuted(nextMuted);
  }, [isMuted, localStream]);

  const toggleCamera = useCallback(() => {
    if (!localStream) {
      return;
    }

    const nextEnabled = !isCameraEnabled;
    localStream.getVideoTracks().forEach((track) => {
      track.enabled = nextEnabled;
    });
    setIsCameraEnabled(nextEnabled);
  }, [isCameraEnabled, localStream]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const handleSessionUpdate = (session: CallSessionRecord) => {
      const partnerId = session.caller_id === user.id ? session.recipient_id : session.caller_id;
      const participant = resolveParticipant(partnerId);

      if (session.status === "ringing" && session.caller_id === user.id && !activeCall) {
        setActiveCall({
          sessionId: session.id,
          partnerId,
          partnerName: participant.name,
          partnerRole: participant.role,
          direction: "outgoing",
          callType: session.call_type,
          status: "ringing",
          createdAt: session.created_at,
        });
        return;
      }

      if (session.status === "ringing" && session.recipient_id === user.id) {
        setIncomingCall({
          sessionId: session.id,
          partnerId,
          partnerName: participant.name,
          partnerRole: participant.role,
          direction: "incoming",
          callType: session.call_type,
          status: "incoming",
          createdAt: session.created_at,
        });
        if (browserPushEnabled) {
          void showBrowserNotification(
            `${session.call_type === "video" ? "Video" : "Voice"} call incoming`,
            {
              body: `${participant.name} is calling you on PropTRUST.`,
              tag: `call-${session.id}`,
              url: `/messages?contact=${partnerId}`,
              requireInteraction: true,
            },
          );
        }
        return;
      }

      if (session.status === "active" && !activeCall) {
        setActiveCall({
          sessionId: session.id,
          partnerId,
          partnerName: participant.name,
          partnerRole: participant.role,
          direction: session.caller_id === user.id ? "outgoing" : "incoming",
          callType: session.call_type,
          status: "connecting",
          createdAt: session.created_at,
        });
      }

      if (activeCall?.sessionId === session.id) {
        if (session.status === "active") {
          clearMissedCallTimer();
          setActiveCall((current) => current ? { ...current, status: "connecting" } : current);
          return;
        }

        if (session.status === "declined") {
          markCallStateEnded("Call declined", `${participant.name} declined the call.`);
          return;
        }

        if (session.status === "missed") {
          markCallStateEnded("Call missed", `${participant.name} did not answer.`);
          return;
        }

        if (session.status === "ended") {
          markCallStateEnded("Call ended", `${participant.name} ended the call.`);
        }
      }

      if (incomingCall?.sessionId === session.id && ["declined", "missed", "ended"].includes(session.status)) {
        setIncomingCall(null);
      }
    };

    const channel = supabase
      .channel(`calls:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "call_sessions",
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          handleSessionUpdate(payload.new as CallSessionRecord);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "call_sessions",
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          handleSessionUpdate(payload.new as CallSessionRecord);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "call_sessions",
          filter: `caller_id=eq.${user.id}`,
        },
        (payload) => {
          handleSessionUpdate(payload.new as CallSessionRecord);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "call_signals",
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          void handleIncomingSignal(payload.new as CallSignalRecord);
        },
      )
      .subscribe();

    const hydrateExistingCalls = async () => {
      const { data: ringingSession } = await supabase
        .from("call_sessions")
        .select("id, caller_id, recipient_id, call_type, status, created_at")
        .eq("recipient_id", user.id)
        .eq("status", "ringing")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (ringingSession) {
        handleSessionUpdate(ringingSession as CallSessionRecord);
        return;
      }

      const { data: activeSession } = await supabase
        .from("call_sessions")
        .select("id, caller_id, recipient_id, call_type, status, created_at")
        .or(`caller_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (activeSession) {
        handleSessionUpdate(activeSession as CallSessionRecord);
      }
    };

    void hydrateExistingCalls();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [
    activeCall?.sessionId,
    browserPushEnabled,
    clearMissedCallTimer,
    handleIncomingSignal,
    incomingCall?.sessionId,
    markCallStateEnded,
    resolveParticipant,
    user,
  ]);

  useEffect(() => {
    if (incomingCall && !activeCall) {
      startRingtone();
      return;
    }

    stopRingtone();
  }, [activeCall, incomingCall, startRingtone, stopRingtone]);

  useEffect(() => {
    return () => {
      teardownConnection();
    };
  }, [teardownConnection]);

  return {
    browserSupportsCalling,
    incomingCall,
    activeCall,
    localStream,
    remoteStream,
    isMuted,
    isCameraEnabled,
    isBusy: Boolean(incomingCall || activeCall),
    startCall,
    acceptIncomingCall,
    declineIncomingCall,
    endActiveCall,
    toggleMute,
    toggleCamera,
    getCallErrorMessage: toErrorMessage,
  };
}
